import { AspectRatio } from '../../../ResizedImage/AspectRatio.csharp';
import { PictureProfile } from '../../../ResizedImage/PictureProfile.csharp';
import { PictureSource } from '../../../ResizedImage/PictureSource.csharp';
import { ResizedImageFormat } from '../../../ResizedImage/ResizedImageFormat.csharp';
import { ScaleMode } from '../../../ResizedImage/ScaleMode.csharp';
import { FocusAreaViewModel } from './FocusAreaViewModel.csharp';
import { ImageCropper } from './imageCropper';
import { ResponsiveImageViewModel } from './ResponsiveImageViewModel.csharp';
import { ResponsivePictureViewModel } from './ResponsivePictureViewModel.csharp';

export class ResponsivePictureBuilder {
    profile: PictureProfile;
    model: ResponsivePictureViewModel;

    constructor(profile: PictureProfile, model: ResponsivePictureViewModel) {
        this.profile = profile;
        this.model = model;
    }

    public getSizes = (): string => {
        return this.profile.sources.map((source) => source.sizes.join(', ')).join(', ');
    };

    public getSrcSet = (source: PictureSource) => {
        return source.allowedWidths.map((width) => this.buildSize(width, source.targetAspectRatio, source.mode, source.quality?.quality)).join(', ');
    };

    public getAllSrcSets = (): string => {
        return this.profile.sources.map(this.getSrcSet).join(', ');
    };

    public getDefaultUrl(): string {
        return this.buildResizedImageUrl(this.model.url || '', this.profile.defaultWidth, this.profile.format);
    }

    public getDefaultData(): { size: number; url: string } {
        const maxSize = Math.max(this.model.responsiveData.width, this.model.responsiveData.height) - 1;
        const defaultSize = this.profile?.sources ? Math.max(...this.profile.sources[0].allowedWidths) : 1024;
        const size = Math.min(maxSize, defaultSize);

        return { url: this.getResizedImageUrl(this.model.url || '', this.profile.format, size, size, ScaleMode.Pad), size };
    }

    private buildSize(width: number, targetAspectRatio: AspectRatio, mode?: ScaleMode, quality?: number): string {
        const url = this.buildResizedImageUrl(
            this.model.url,
            width,
            this.profile.format,
            mode,
            targetAspectRatio,
            quality,
            this.model.responsiveData,
            this.profile.maxImageDimension
        );

        return `${url} ${width}w`;
    }

    public getResizedImageUrl(
        imageUrl: string,
        format: ResizedImageFormat = ResizedImageFormat.Preserve,
        width: number | null = null,
        height: number | null = null,
        mode: ScaleMode | null = null,
        quality: number | null = null,
        crop: string | null = null
    ) {
        const urlParams = [];

        if (width) {
            urlParams.push(`width=${width}`);
        }

        if (height) {
            urlParams.push(`height=${height}`);
        }

        if (mode) {
            const modeString = ScaleMode[mode];
            urlParams.push(`rmode=${modeString}`);
        }

        if (quality) {
            urlParams.push(`quality=${quality.toString()}`);
        }

        if (crop) {
            urlParams.push(`crop=${crop}`);
        }

        if (format != ResizedImageFormat.Preserve) {
            urlParams.push(`format=${ResizedImageFormat[format].toLowerCase()}`);
        }

        const concatenationSign = imageUrl.includes('?') ? '&' : '?';

        return urlParams.length > 0 ? imageUrl + concatenationSign + urlParams.join('&') : imageUrl;
    }

    private buildResizedImageUrl(
        imageUrl: string,
        width: number,
        format?: ResizedImageFormat,
        mode?: ScaleMode,
        targetAspectRatio?: AspectRatio,
        quality?: number,
        image?: ResponsiveImageViewModel,
        maxImageDimension?: number
    ) {
        if (image == null) return imageUrl;

        if (mode !== ScaleMode.Default && mode !== ScaleMode.Max && (targetAspectRatio == null || targetAspectRatio.ratio < 0)) {
            throw `Error for image ${imageUrl}. Aspect ratio is required when ScaleMode is other than Max`;
        }

        const originalAspectRatio = {
            ratio: image.width / image.height,
            hasValue: true
        };

        maxImageDimension = maxImageDimension || 3200;

        width = Math.min(width, maxImageDimension);
        let height = null;
        let crop = null;

        if (targetAspectRatio) {
            height = width / targetAspectRatio.ratio;
        } else {
            const dimension = width / originalAspectRatio.ratio;
            if (dimension > maxImageDimension) {
                height = dimension;
            }
        }

        if (height && height > maxImageDimension) {
            const ratio = width / height;
            height = maxImageDimension;
            width = height * ratio;
        }

        if (image.focusArea && image.shouldUseFocusArea && mode !== ScaleMode.Crop) {
            crop = this.buildCrop(image.focusArea);
        }

        if (mode == ScaleMode.Crop && targetAspectRatio) {
            crop = ImageCropper.getCropSettings(targetAspectRatio, image);
        }

        return this.getResizedImageUrl(imageUrl, format, width, height, mode, quality, crop);
    }

    private buildCrop(focusArea: FocusAreaViewModel) {
        return `${focusArea.left},${focusArea.top},${focusArea.right},${focusArea.bottom}`;
    }
}
